import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, Shield, Globe, Cpu, LineChart, Layers, Sparkles, Clock, ChevronRight, Check } from "lucide-react";
import GradientBg from "@/components/common/GradientBg";
import HeadMeta from "@/components/common/HeadMeta";
import api from "@/lib/api";
import { BRAND } from "@/branding.config";
import AnimatedCounter from "@/components/marketing/AnimatedCounter";
import LatencyPulse from "@/components/marketing/LatencyPulse";
import LatencyComparison from "@/components/marketing/LatencyComparison";
import Testimonials from "@/components/marketing/Testimonials";
import Reveal from "@/components/marketing/Reveal";
import CinematicHero from "@/components/marketing/CinematicHero";

const HERO_STATS = [
  { label: "Uptime SLA",      value: 99.99, decimals: 2, suffix: "%" },
  { label: "Deploy time",     value: 60,    decimals: 0, suffix: "s", prefix: "<" },
  { label: "Global regions",  value: 12,    decimals: 0 },
  { label: "DDoS mitigation", value: 1.5,   decimals: 1, suffix: "Tbps" },
];

const FEATURES = [
  { icon: Zap,       title: "Sub-1ms to broker",     body: "Instances pinned next to LD4, NY4 and TY3 with dedicated cross-connects." },
  { icon: Shield,    title: "Always-on DDoS",        body: "1.5 Tbps global scrubbing at every edge, without traffic penalty." },
  { icon: Cpu,       title: "NVMe & dedicated cores",body: "Elite tier gets pinned vCPU — no noisy neighbours, guaranteed IOPS." },
  { icon: Globe,     title: "12 tier-3 regions",     body: "Frankfurt, London, NY, LA, Miami, Amsterdam, Zurich, Tokyo, HK, Singapore, Sydney, São Paulo." },
  { icon: LineChart, title: "Live monitoring",       body: "CPU, RAM, disk, network — everything streams to your dashboard in real time." },
  { icon: Layers,    title: "Windows & Linux",       body: "Windows Server 2019/2022, Ubuntu LTS, Debian, Rocky, Alma — all pre-licensed." },
];

const LOGOS = ["MetaQuotes", "cTrader", "NinjaTrader", "MT4", "MT5", "TradingView", "Bloomberg", "Refinitiv"];

export default function Home() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/public/plans").then((r) => setPlans(r.data)).catch(() => setPlans([])); }, []);
  const featured = plans.filter((p) => p.popular).slice(0, 2);

  return (
    <>
      <HeadMeta title={null} description={BRAND.default_meta_description} />

      {/* HERO — Cinematic redesign (Phase 8B) */}
      <CinematicHero />

      {/* Original hero preserved below for reference; hidden. */}
      <section className="relative overflow-hidden hidden">
        <GradientBg variant="hero" />
        <div className="absolute right-[-120px] top-24 w-[520px] h-[520px] opacity-70 pointer-events-none hidden md:block">
          <LatencyPulse />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 pt-28 pb-32">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-background/40 backdrop-blur px-3 py-1 text-[11px] tracking-wide"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-muted-foreground">All systems operational · 99.998% uptime · updated live</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
            className="mt-8 max-w-4xl text-5xl md:text-7xl lg:text-8xl font-black tracking-[-0.03em] leading-[0.94]"
          >
            The VPS your <span className="brand-gradient-text">algorithms</span> deserve.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
            className="mt-6 max-w-xl text-lg text-muted-foreground leading-relaxed"
          >
            Ultra-low latency Forex and Windows VPS, engineered for traders and enterprise Windows workloads.
            Deployed in under a minute, from twelve tier-3 regions.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <Link to="/pricing"  className="btn-primary px-6 py-3 text-sm" data-testid="hero-cta-pricing">Explore pricing <ArrowUpRight size={16} /></Link>
            <Link to="/register" className="btn-outline px-6 py-3 text-sm" data-testid="hero-cta-register">Start free trial</Link>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-2">
              <Check size={14} className="text-emerald-500" /> No credit card required
            </div>
          </motion.div>

          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-5">
            {HERO_STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 + i * 0.08 }}
                className="glass p-6"
              >
                <div className="font-display text-3xl md:text-4xl font-black tracking-tight">
                  <AnimatedCounter value={s.value} decimals={s.decimals} suffix={s.suffix || ""} prefix={s.prefix || ""} />
                </div>
                <div className="mt-1.5 text-xs uppercase tracking-[0.18em] text-muted-foreground">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <section className="py-14 border-y border-white/[0.06]">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Trusted by trading desks and platforms worldwide</div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-70">
            {LOGOS.map((l) => (<div key={l} className="font-display text-lg md:text-xl font-bold tracking-tight text-muted-foreground">{l}</div>))}
          </div>
        </div>
      </section>

      {/* FEATURES bento */}
      <section className="mx-auto max-w-7xl px-6 py-32">
        <Reveal>
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">The platform</div>
              <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">Everything a serious operator needs.</h2>
              <p className="mt-3 max-w-xl text-muted-foreground">
                {BRAND.app_name} is built by ex-market-infra engineers who understand latency budgets, jitter and packet loss — not just uptime dashboards.
              </p>
            </div>
            <Link to="/features" className="hidden md:inline-flex btn-ghost text-sm">All features <ChevronRight size={14} /></Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <Reveal className="md:col-span-7">
            <div className="glass p-8 md:p-10 relative overflow-hidden min-h-[320px] h-full">
              <div className="orb bg-violet w-[300px] h-[300px] -right-20 -top-20 opacity-40" />
              <div className="relative">
                <Sparkles className="text-primary" size={22} />
                <h3 className="mt-4 text-2xl md:text-3xl font-bold tracking-tight max-w-md">Broker-adjacent servers. Predictable execution. Zero drama.</h3>
                <p className="mt-3 text-muted-foreground max-w-md">
                  Every Forex tier deploys inside carrier-neutral facilities with private peering to major FX ECNs. Nothing between your EA and price. Nothing.
                </p>
                <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg">
                  <div><div className="font-mono text-2xl font-bold">0.42ms</div><div className="text-[11px] uppercase tracking-widest text-muted-foreground">to LD4</div></div>
                  <div><div className="font-mono text-2xl font-bold">0.61ms</div><div className="text-[11px] uppercase tracking-widest text-muted-foreground">to NY4</div></div>
                  <div><div className="font-mono text-2xl font-bold">0.88ms</div><div className="text-[11px] uppercase tracking-widest text-muted-foreground">to TY3</div></div>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="md:col-span-5 grid grid-cols-1 gap-4 md:gap-6">
            <Reveal delay={0.1}><div className="glass p-8 relative overflow-hidden"><Shield className="text-pink" size={22}/><h3 className="mt-4 text-xl font-bold">Enterprise security by default</h3><p className="mt-2 text-sm text-muted-foreground">SOC 2 · ISO 27001 · GDPR · Encrypted at rest and in transit · Hardware-backed key isolation.</p></div></Reveal>
            <Reveal delay={0.2}><div className="glass p-8 relative overflow-hidden"><Clock className="text-primary" size={22}/><h3 className="mt-4 text-xl font-bold">60-second deploy</h3><p className="mt-2 text-sm text-muted-foreground">Pick a template, region and plan — your VPS is running before your coffee cools.</p></div></Reveal>
          </div>

          {FEATURES.map(({ icon: Icon, title, body }, i) => (
            <Reveal key={title} delay={i * 0.06} className="md:col-span-4">
              <div className="surface p-6 h-full transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-primary/40">
                <Icon className="text-primary" size={20}/>
                <h4 className="mt-4 text-lg font-bold">{title}</h4>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <LatencyComparison />
      <Testimonials />

      {/* Pricing preview */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-24">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Pricing</div>
              <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">Straight-forward, transparent, no surprises.</h2>
              <p className="mt-3 text-muted-foreground">Only pay for what you deploy. Upgrade or cancel any time from the portal.</p>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <div className="glass p-8 relative overflow-hidden border-primary/30 glow-violet">
                  <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full bg-primary/15 text-primary border border-primary/25">Most popular</span>
                  <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-bold">{p.category}</div>
                  <h3 className="mt-2 text-3xl font-black tracking-tight">{p.name}</h3>
                  <div className="mt-4 flex items-baseline gap-1"><span className="font-display text-5xl font-black">${p.price_monthly}</span><span className="text-sm text-muted-foreground">/month</span></div>
                  <ul className="mt-6 space-y-2.5 text-sm">
                    <li className="flex items-center gap-2"><Cpu size={14} className="text-primary"/> {p.cpu} vCPU · {p.ram_gb} GB RAM</li>
                    <li className="flex items-center gap-2"><Layers size={14} className="text-primary"/> {p.storage_gb} GB NVMe · {p.bandwidth_tb} TB traffic</li>
                    {p.features.slice(0, 3).map((f) => (<li key={f} className="flex items-center gap-2 text-muted-foreground"><Check size={14} className="text-emerald-500" /> {f}</li>))}
                  </ul>
                  <Link to="/pricing" data-testid={`home-plan-${p.slug}`} className="btn-primary mt-8 w-full py-3">Choose {p.name}</Link>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Enterprise CTA */}
      <section className="mx-auto max-w-6xl px-6 py-24">
        <Reveal>
          <div className="glass-strong p-10 md:p-14 relative overflow-hidden">
            <div className="orb bg-violet w-[380px] h-[380px] -left-24 -top-24" />
            <div className="orb bg-pink   w-[280px] h-[280px] -right-16 -bottom-16" />
            <div className="relative grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Enterprise</div>
                <h3 className="mt-3 text-3xl md:text-4xl font-black tracking-tight">Dedicated hardware, contractual SLA, MSA on request.</h3>
                <p className="mt-3 text-muted-foreground max-w-xl">For prop shops, brokers and regulated firms that need infrastructure with a written promise.</p>
              </div>
              <div className="flex md:justify-end">
                <Link to="/enterprise" className="btn-primary px-6 py-3">Learn more <ArrowUpRight size={16}/></Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-6 py-32">
        <Reveal>
          <div className="relative glass-strong p-10 md:p-16 overflow-hidden">
            <div className="orb bg-violet w-[420px] h-[420px] -left-32 -top-32" />
            <div className="orb bg-pink w-[320px] h-[320px] -right-20 -bottom-20" />
            <div className="relative">
              <h2 className="text-4xl md:text-6xl font-black tracking-tight max-w-3xl">Stop paying for latency you can't measure.</h2>
              <p className="mt-5 max-w-2xl text-lg text-muted-foreground">Start a {BRAND.app_name} instance now — deployed in seconds, from the region closest to your broker.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" className="btn-primary px-6 py-3" data-testid="cta-register">Deploy a VPS</Link>
                <Link to="/contact"  className="btn-outline px-6 py-3" data-testid="cta-contact">Talk to sales</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
